import Breadcrumb, { Crumb, Separator } from "@asphalt-react/breadcrumb";
import Button from "@asphalt-react/button";
import Dropdown from "@asphalt-react/dropdown";
import Flag from "@asphalt-react/flag";
import Stack from "@asphalt-react/stack";
import TextField from "@asphalt-react/textfield";
import React, { useContext, useEffect, useState } from "react";
import { useParams, useHistory, Link, useLocation } from "react-router-dom";
import { UserContext } from "../../common/hooks/UserContext";
import routes from "../../common/models/route-constants";
import LoadingPage from "../../common/pages/LoadingPage";
import { getIconDetails, updateIcon } from "../api/iconsApi";
import AssetSelector from "../../common/components/AssetSelector";
import styles from "./UpdateIconPage.module.css";
import { validIconFileSize } from "../validations/iconValidations";
import { formatTime } from "./../../common/utils/generalUtils";
import { useUser } from "../../authentication/api/userApi";


function UpdateIconPage() {
  const history = useHistory();
  const location = useLocation();

  // Context state
  const { user } = useContext(UserContext);

  // Input value states
  const [selectedIconCategory, setSelectedIconCategory] = useState("");
  const [selectedIconSize, setSelectedIconSize] = useState("");
  const [iconName, setIconName] = useState(null);
  const [iconFile, setIconFile] = useState(null);

  // Error states
  const [iconCategoryDropdownError, setIconCategoryDropdownError] =
    useState(null);
  const [iconSizeDropdownError, setIconSizeDropdownError] = useState(null);
  const [iconNameError, setIconNameError] = useState(null);
  const [assetSelectorError, setAssetSelectorError] = useState(null);

  // Icon State
  const [iconDetailsObj, setIconDetailsObj] = useState(null);
  const updatedBy = useUser(iconDetailsObj?.last_updated_by);
  const createdBy = useUser(iconDetailsObj?.created_by);

  // Page loading state
  const [isLoading, setIsLoading] = useState(true);

  // Notification state
  const [notification, setNotification] = useState(null);

  // Params state
  const { id } = useParams();

  function buildNotification() {
    return {
      title: "Icon updated",
      content: `Icon for ${iconDetailsObj.combined_name} has been updated successfully`,
    };
  }

  const clearHistoryNotificationState = (history) => {
    let state = { ...history.location.state };
    delete state.notification;
    history.replace({
      ...history.location,
      state,
    });
  };

  // Cancel button click
  const handeCancelClick = () => {
    //TODO : Check for any changes and give user a prompt
    history.goBack();
  };

  // Save Button click
  const handleSaveClick = async () => {
    //Check if icon file is selected
    if (iconFile) {
      setAssetSelectorError(null);
    } else {
      setAssetSelectorError("A file must be selected");
    }

    if (iconFile) {
      // Check if icon file dimensions are the same as the icon size selected
      const isIconFileSizeValid = await validIconFileSize(
        iconFile,
        selectedIconSize.key
      );

      if (isIconFileSizeValid) {
        setIsLoading(true);
        try {
          await updateIcon(
            user.uid,
            id,
            iconFile,
            iconDetailsObj.combined_name
          );
          setIsLoading(false);
          history.push(`/${routes.ICONS}/${routes.ASSETS}/${id}`, {
            notification: buildNotification(),
          });
        } catch (error) {
          setIsLoading(false);
          if (error.name === "FirebaseError") {
            console.log(error.message);
          } else if (
            error.message === "Icon with the same name already exists"
          ) {
            setIconNameError(error.message);
          }
        }
      } else {
        setIsLoading(false);
        setAssetSelectorError(
          `Dimensions of Icon file must be ${selectedIconSize.key} x ${selectedIconSize.key}`
        );
      }
    }
  };

  useEffect(() => {
    getIconDetails(id)
      .then((doc) => {
        setIconDetailsObj(doc);
        setSelectedIconCategory({
          id: "0",
          key: doc.name.icon_category,
        });
        setSelectedIconSize({
          id: "0",
          key: doc.name.icon_size,
        });
        setIconName(doc.name.icon_name);
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
      });
    if (location.state && location["state"].notification) {
      setNotification(location.state.notification);
    }
  }, [setIconDetailsObj, setIsLoading, id, location]);

  return (
    <React.Fragment>
      {isLoading ? (
        <LoadingPage />
      ) : (
        <React.Fragment>
          <div className={`panel ${notification ? "show" : ""}`}>
            <div className={`animateFlag ${notification ? "visible" : ""}`}>
              <Flag
                success
                floating
                title={notification ? notification.title : ""}
                content={notification ? notification.content : ""}
                onDismiss={() => {
                  setNotification(null);
                  clearHistoryNotificationState(history);
                }}
              />
            </div>
          </div>
          <div className={styles.updatePageContainer}>
            <div className={styles.breadcrumbContainer}>
              <Breadcrumb>
                <Crumb
                  text="assets"
                  tagProps={{
                    to: `/${routes.ICONS}/${routes.ASSETS}`,
                  }}
                  as={Link}
                />
                <Separator />
                <Crumb
                  text={iconDetailsObj.combined_name}
                  tagProps={{
                    to: `/${routes.ICONS}/${routes.ASSETS}/${id}`,
                  }}
                  as={Link}
                />
              </Breadcrumb>
            </div>
            <div>
              <h3 className={styles.heading}>{iconDetailsObj.combined_name}</h3>
              <div className={styles.timestapWrapper}>
                <div className={styles.timeRow}>
                  <b>Last updated at</b>
                  <p>{formatTime(iconDetailsObj.last_updated_at)}</p>
                </div>
                <div className={styles.timeRow}>
                  <b>Created at</b>
                  <p>{formatTime(iconDetailsObj.created_at)}</p>
                </div>

                <div className={styles.timeRow}>
                <b>Last updated by</b>
                <p>{updatedBy ? `${updatedBy.name}` : "Loading..."}</p>
                </div>

                <div className={styles.timeRow}>
                <b>Created by</b>
                <p>{createdBy ? `${createdBy.name}` : "Loading..."}</p>
                </div>

                <div className={styles.timeRow}>
                <b>Aloha Version</b>
                <p>{iconDetailsObj.android?.aloha_version ?? "NA"}</p>
                </div>

                <div className={styles.timeRow}>
                <b>Aloha Release Date</b>
                <p>{iconDetailsObj.android?.date
                    ? formatTime(iconDetailsObj.android.date)
                    : "NA"}</p>
                </div>
              </div>
              <h5 className={styles.subHeading}>Edit Icon</h5>
              <div className={styles.formRow}>
                <div className={styles.iconCategoryContainer}>
                  <p className={styles.inputLabel}>Icon Category</p>
                  <div className={styles.inputContainer}>
                    <Dropdown
                      items={[selectedIconCategory]}
                      onChange={(selectedIconCategory) => {
                        setSelectedIconCategory(selectedIconCategory);
                        setIconCategoryDropdownError(null);
                      }}
                      stretch
                      invalid={!!iconCategoryDropdownError}
                      disabled={true}
                      initialSelectedItem={selectedIconCategory}
                    />
                  </div>
                  {iconCategoryDropdownError ? (
                    <Flag error title={iconCategoryDropdownError} />
                  ) : null}
                </div>
                <div className={styles.iconSizeContainer}>
                  <p className={styles.inputLabel}>Icon Size</p>
                  <div className={styles.inputContainer}>
                    <Dropdown
                      items={[selectedIconSize]}
                      onChange={(selectedIconSize) => {
                        setSelectedIconSize(selectedIconSize);
                        setIconSizeDropdownError(null);
                      }}
                      invalid={!!iconSizeDropdownError}
                      stretch
                      disabled={true}
                      initialSelectedItem={selectedIconSize}
                    />
                  </div>
                  {iconSizeDropdownError ? (
                    <Flag error title={iconSizeDropdownError} />
                  ) : null}
                </div>
                <div className={styles.iconNameContainer}>
                  <p className={styles.inputLabel}>Icon Name</p>
                  <div className={styles.inputContainer}>
                    <TextField
                      required={!!iconNameError}
                      size="m"
                      placeholder="Icon Name"
                      type="text"
                      disabled={true}
                      stretch
                      onChange={(e) => {
                        setIconName(e.target.value);
                        setIconNameError(null);
                      }}
                      pattern="[a-z\d_]+"
                      value={iconName}
                    />
                  </div>
                  {iconNameError ? <Flag error title={iconNameError} /> : null}
                </div>
              </div>
              <div className={styles.assetSelectorContainer}>
                <div className={styles.inputContainer}>
                  <AssetSelector
                    heading="Icon"
                    asset={iconDetailsObj.img_url}
                    onAssetSelected={(file) => {
                      if (file) {
                        setIconFile(file);
                        setAssetSelectorError(null);
                      }
                    }}
                  />
                </div>
                {assetSelectorError ? (
                  <Flag error title={assetSelectorError} />
                ) : null}
              </div>
              <Stack>
                <Button primary onClick={handleSaveClick}>
                  Save
                </Button>
                <Button secondary onClick={handeCancelClick}>
                  Cancel
                </Button>
              </Stack>
            </div>
          </div>
        </React.Fragment>
      )}
    </React.Fragment>
  );
}

export default UpdateIconPage;
